import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { ChevronDown, Ruler } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Popover, type PopoverState } from 'react-tiny-popover';
import { SearchInput } from '~/components/common/SearchInput';
import { EmptyState } from '~/components/ui/EmptyState';
import { RetryBanner } from '~/components/ui/RetryBanner';
import { popoverMaxHeight } from '~/components/ui/popoverMaxHeight';
import { useCombobox } from '~/hooks/useCombobox';
import { useDebouncedSearchInput } from '~/hooks/useDebouncedSearchInput';
import { usePaginatedQuery } from '~/hooks/usePaginatedQuery';
import { useCannedTallyCandidateLengths } from '~/api/canned-tally';
import type { usePagedLengths } from '~/api/productLengths';
import type { CannedTallyCandidateLengthsQuery } from '~/graphql/graphql';
import { cn } from '~/utils';

export type ProductLengthPickerLabels = {
  label: string;
  trigger: string;
  searchPlaceholder: string;
  searchLabel: string;
  loading: string;
  error: string;
  emptyTitle: string;
  emptyMessage: string;
  noResultsTitle: string;
  noResultsMessage: string;
};

type LengthOption = { length: { id: string; displayValue: string } };

export type PickerLeadingOption = Readonly<{
  id: string;
  searchText: string;
  content: ReactNode;
  onSelect: () => void;
}>;

type CandidateOption =
  CannedTallyCandidateLengthsQuery['lengths']['content'][number];

type LengthsFetchHook<TOption extends LengthOption> = (
  ...args: Parameters<typeof useCannedTallyCandidateLengths>
) => ReturnType<typeof usePagedLengths<TOption>>;

type LengthsSource<TOption extends LengthOption> =
  | {
      fetchHook: LengthsFetchHook<TOption>;
      platformProductId: string;
      lengths?: never;
    }
  | {
      lengths: readonly (TOption & { id: string })[];
      fetchHook?: never;
      platformProductId?: never;
    };

export type ProductLengthPickerProps<
  TOption extends LengthOption = CandidateOption,
> = Readonly<
  LengthsSource<TOption> & {
    labels: ProductLengthPickerLabels;
    testId: string;
    onSelect: (candidate: TOption & { id: string }) => void;
    isOfferable?: (candidate: TOption & { id: string }) => boolean;
    disabled?: boolean;
    pageSize?: number;
    className?: string;
    leadingOptions?: readonly PickerLeadingOption[];
    selectedIds?: ReadonlySet<string>;
  }
>;

const DEFAULT_PAGE_SIZE = 20;
const EVERY_CANDIDATE_OFFERABLE = () => true;
const NO_LEADING_OPTIONS: readonly PickerLeadingOption[] = [];

type PickerItem<TOption> =
  | { kind: 'leading'; option: PickerLeadingOption }
  | { kind: 'candidate'; candidate: TOption & { id: string } };

function matchesSearch(text: string, search: string) {
  return text.toLowerCase().includes(search.trim().toLowerCase());
}

/**
 * Dropdown that picks one length a product can be quoted or tallied on.
 *
 * Owns the debounced search, the paged query and the loading, empty, no-match and error
 * states, and fetches only while its panel is open. It keeps no selection: a pick calls
 * `onSelect` and the control returns to its placeholder, ready for the next one.
 *
 * `isOfferable` filters the fetched page, so a page can show fewer than `pageSize` rows.
 *
 * `lengths` replaces `fetchHook` and `platformProductId` with a list the caller already
 * holds: the search filters it here, and nothing is fetched.
 *
 * `leadingOptions` are listed before the fetched lengths and filtered here by their
 * `searchText`, not by the server. A pick calls the option's own `onSelect`.
 *
 * `selectedIds` lets the buyer tick several lengths: each listed length shows whether its
 * id is in the set, and picking one calls `onSelect` without closing the panel or
 * clearing the search, so the caller can toggle it. A leading option still closes the
 * panel.
 *
 * @example
 *   <ProductLengthPicker
 *     fetchHook={useCannedTallyCandidateLengths}
 *     platformProductId={group.productId}
 *     testId={`${group.productId}-add-length`}
 *     labels={addLengthLabels}
 *     className="w-full md:w-1/2 lg:w-1/3"
 *     isOfferable={(candidate) => candidate.platformProductLength != null}
 *     onSelect={(candidate) => addRow(candidate)}
 *   />
 */
export function ProductLengthPicker<
  TOption extends LengthOption = CandidateOption,
>({
  labels,
  testId,
  onSelect,
  isOfferable = EVERY_CANDIDATE_OFFERABLE,
  disabled = false,
  pageSize = DEFAULT_PAGE_SIZE,
  className,
  fetchHook,
  platformProductId,
  lengths,
  leadingOptions = NO_LEADING_OPTIONS,
  selectedIds,
}: ProductLengthPickerProps<TOption>) {
  const { t } = useTranslation('general');
  const { searchInputValue, activeSearch, handleSearchChange, setSearch } =
    useDebouncedSearchInput();
  const [isOpen, setIsOpen] = useState(false);
  if (disabled && isOpen) setIsOpen(false);
  const triggerId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [searchInput, setSearchInput] = useState<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const closePanel = () => setIsOpen(false);

  const closePanelAndFocusTrigger = () => {
    closePanel();
    triggerRef.current?.focus();
  };

  const filters = useMemo(
    () => ({ platformProductId, search: activeSearch }),
    [platformProductId, activeSearch]
  );

  const {
    data: candidates,
    sentinelRef,
    isLoading,
    error,
    refetch,
  } = usePaginatedQuery({
    // idle stand-in so the hook runs on every render; `enabled` keeps it from fetching
    fetchHook:
      fetchHook ??
      (useCannedTallyCandidateLengths as unknown as LengthsFetchHook<TOption>),
    select: (data) => data?.lengths,
    options: { pageSize, filters, enabled: isOpen && !lengths },
  });

  const offerableCandidates = (
    lengths?.filter((candidate) =>
      matchesSearch(candidate.length.displayValue, activeSearch)
    ) ?? candidates
  ).filter(isOfferable);
  const hasCandidates = offerableCandidates.length > 0;
  const isSearching = activeSearch.trim().length > 0;
  const matchingLeadingOptions = leadingOptions.filter((option) =>
    matchesSearch(option.searchText, activeSearch)
  );
  const items: PickerItem<TOption>[] = [
    ...matchingLeadingOptions.map((option) => ({
      kind: 'leading' as const,
      option,
    })),
    ...offerableCandidates.map((candidate) => ({
      kind: 'candidate' as const,
      candidate,
    })),
  ];

  const {
    listboxId,
    safeActiveIndex,
    select: choose,
    handleInputChange,
    handleKeyDown: handlePanelKeyDown,
    inputProps,
    optionProps,
  } = useCombobox({
    items,
    term: activeSearch,
    isOpen,
    onRequestClose: closePanelAndFocusTrigger,
    keepsOpenAfterSelecting: (item) =>
      !!selectedIds && item.kind === 'candidate',
    onSelect: (item) => {
      if (item.kind === 'leading') item.option.onSelect();
      else onSelect(item.candidate);
      if (!selectedIds || item.kind === 'leading') setSearch('');
    },
  });

  const [leadingCountOfHighlight, setLeadingCountOfHighlight] = useState(
    matchingLeadingOptions.length
  );
  if (leadingCountOfHighlight !== matchingLeadingOptions.length) {
    setLeadingCountOfHighlight(matchingLeadingOptions.length);
    handleInputChange();
  }

  useEffect(() => {
    if (searchInput) searchInput.focus();
  }, [searchInput]);

  useEffect(() => {
    if (safeActiveIndex < 0) return;
    listRef.current?.children[safeActiveIndex]?.scrollIntoView({
      block: 'nearest',
    });
  }, [safeActiveIndex]);

  const handleTriggerKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key !== 'ArrowDown') return;
    event.preventDefault();
    setIsOpen(true);
  };

  const itemKey = (item: PickerItem<TOption>) =>
    item.kind === 'leading' ? item.option.id : item.candidate.id;

  const renderList = () => (
    <ul
      ref={listRef}
      id={listboxId}
      role="listbox"
      aria-label={labels.label}
      aria-multiselectable={selectedIds ? true : undefined}
      data-testid={`${testId}-list`}
      className="flex flex-col py-1"
    >
      {items.map((item, index) => (
        <li
          key={itemKey(item)}
          {...optionProps(index)}
          {...(selectedIds && {
            'aria-selected':
              item.kind === 'candidate' && selectedIds.has(item.candidate.id),
          })}
          data-testid={`${testId}-option-${itemKey(item)}`}
          className={cn(
            'cursor-pointer px-3 py-2 text-sm text-neut-prim',
            index === safeActiveIndex ? 'bg-prim-50' : 'hover:bg-neut-bg-alt'
          )}
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => choose(item)}
        >
          {item.kind === 'leading' ? (
            item.option.content
          ) : (
            <span className="flex items-center gap-2">
              {selectedIds && (
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-prim pointer-events-none"
                  checked={selectedIds.has(item.candidate.id)}
                  readOnly
                  tabIndex={-1}
                  aria-hidden="true"
                />
              )}
              {item.candidate.length.displayValue}
            </span>
          )}
        </li>
      ))}
    </ul>
  );

  const renderLengthsStatus = () => {
    if (error && !lengths) {
      return (
        <RetryBanner
          message={labels.error}
          retryLabel={t('general.retry')}
          onRetry={() => void refetch()}
          testId={testId}
          className="px-3 py-6"
        />
      );
    }

    if (isLoading && !hasCandidates && !lengths) {
      return (
        <p
          className="px-3 py-6 text-center text-sm text-neut-sec"
          data-testid={`${testId}-loading`}
        >
          {labels.loading}
        </p>
      );
    }

    if (items.length === 0) {
      return (
        <div
          data-testid={isSearching ? `${testId}-no-results` : `${testId}-empty`}
        >
          <EmptyState
            size="compact"
            icon={<Ruler size={24} aria-hidden="true" />}
            title={isSearching ? labels.noResultsTitle : labels.emptyTitle}
            message={
              isSearching ? labels.noResultsMessage : labels.emptyMessage
            }
          />
        </div>
      );
    }

    if (lengths) return null;

    return <div ref={sentinelRef} data-testid={`${testId}-sentinel`} />;
  };

  const renderBody = () => (
    <>
      {items.length > 0 && renderList()}
      {renderLengthsStatus()}
    </>
  );

  return (
    <Popover
      isOpen={isOpen}
      onClickOutside={closePanel}
      positions={['bottom', 'top']}
      align="start"
      containerStyle={{ zIndex: '1100' }}
      content={(popoverState: PopoverState) => (
        <div
          data-testid={`${testId}-panel`}
          tabIndex={-1}
          className="mt-2 w-72 outline-none overflow-y-auto rounded-xl border border-neut-border bg-neut-light p-2 shadow-xl"
          style={{ maxHeight: popoverMaxHeight(popoverState) }}
          onKeyDown={handlePanelKeyDown}
        >
          <SearchInput
            value={searchInputValue}
            onChange={(event) => {
              handleInputChange();
              handleSearchChange(event);
            }}
            placeholder={labels.searchPlaceholder}
            ariaLabel={labels.searchLabel}
            testId={`${testId}-search`}
            widthClassName="w-full"
            inputRef={setSearchInput}
            comboboxProps={inputProps}
          />
          {renderBody()}
        </div>
      )}
    >
      <div
        className={cn('flex flex-col gap-1', className)}
        data-testid={testId}
      >
        <label
          htmlFor={triggerId}
          className="text-xs font-medium text-neut-sec"
        >
          {labels.label}
        </label>
        <button
          id={triggerId}
          ref={triggerRef}
          type="button"
          data-testid={`${testId}-trigger`}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-controls={listboxId}
          disabled={disabled}
          className="flex w-full cursor-pointer items-center justify-between gap-2 rounded-md border border-neut-border bg-neut-bg px-3 py-2 text-left text-sm text-neut-sec hover:border-prim-300 disabled:cursor-not-allowed disabled:opacity-50"
          onClick={() => setIsOpen((wasOpen) => !wasOpen)}
          onKeyDown={handleTriggerKeyDown}
        >
          <span className="truncate">{labels.trigger}</span>
          <ChevronDown size={16} aria-hidden="true" className="shrink-0" />
        </button>
      </div>
    </Popover>
  );
}
